import { lazy } from 'react';

import { Loadable } from '@trendiolive/trendio-selfcare-ui-kit';

// project import
import DashboardLayout from 'layout/Dashboard';
import PagesLayout from 'layout/Pages';

import { COMMISSION_OPTIMIZER_PATH, SHOP_HEALTH_PATH } from 'constants/routeConstants';

const DataAnalytics = Loadable(lazy(() => import('pages/data-analytics')));
const DataAnalyticsTiktokTab = Loadable(lazy(() => import('features/data-analytics/components/TTSDashboardPresentationResult')));
const DataAnalyticsYoutubeTab = Loadable(lazy(() => import('features/data-analytics/components/YTSDashboardPresentationResult')));
const PerformanceOverviewTab = Loadable(
  lazy(() => import('features/data-analytics/components/scorecards/PerformanceOverview/PerformanceOverviewTab'))
);
const PerformanceDashboardTab = Loadable(
  lazy(() => import('features/data-analytics/components/scorecards/PerformanceDashboard/PerformanceDashboardTab'))
);
const OutreachPerformanceTab = Loadable(
  lazy(() => import('features/data-analytics/components/scorecards/OutreachPerformance/OutreachPerformanceTab'))
);
const AdPerformanceTab = Loadable(lazy(() => import('features/data-analytics/components/scorecards/AdPerformance/AdPerformanceTab')));
const ProductPerformanceTab = Loadable(
  lazy(() => import('features/data-analytics/components/scorecards/ProductPerformance/ProductPerformanceTab'))
);
const SamplesByTierDashboardTab = Loadable(
  lazy(() => import('features/data-analytics/components/scorecards/SamplesByTierDashboard/SamplesByTierDashboardTab'))
);
const CollaborationPerformanceTab = Loadable(
  lazy(() => import('features/data-analytics/components/scorecards/CampaignsOverview/CollaborationPerformanceTab'))
);
const VideoPerformanceTab = Loadable(
  lazy(() => import('features/data-analytics/components/scorecards/VideoPerformance/VideoPerformanceTab'))
);
const TopCreatorsTab = Loadable(lazy(() => import('features/data-analytics/components/scorecards/TopCreators/TopCreatorsTab')));

const BestSellingProducts = Loadable(lazy(() => import('pages/market-intelligence/best-selling-products')));
const BestSellingCreators = Loadable(lazy(() => import('pages/market-intelligence/best-selling-creators')));
const BestSellingVideos = Loadable(lazy(() => import('pages/market-intelligence/best-selling-videos')));
const BestSellingLives = Loadable(lazy(() => import('pages/market-intelligence/best-selling-lives')));

const Faqs = Loadable(lazy(() => import('pages/support/faqs')));
const Help = Loadable(lazy(() => import('pages/support/help')));
const Home = Loadable(lazy(() => import('pages/home')));
const Settings = Loadable(lazy(() => import('pages/settings')));
const ShopHealth = Loadable(lazy(() => import('pages/shop-health')));
const CommissionOptimizer = Loadable(lazy(() => import('pages/business-strategy/commission-optimizer')));

const Pricing = Loadable(lazy(() => import('pages/pricing/pricing')));
const Error404 = Loadable(lazy(() => import('pages/maintenance/404')));
const AddPayment = Loadable(lazy(() => import('pages/add-payment')));

const VideoAi = Loadable(lazy(() => import('pages/video-ai')));
const VideoArchetypes = Loadable(lazy(() => import('pages/video-archetypes')));

const AffiliatesFinder = Loadable(lazy(() => import('pages/affiliate-community/affiliates-finder')));
const AffiliateCommunity = Loadable(lazy(() => import('pages/affiliate-community/my-community')));
const CampaignSetup = Loadable(lazy(() => import('pages/collaborations/campaign-setup')));
const CollaborationReview = Loadable(lazy(() => import('pages/collaborations/collaboration-review')));
const CampaignDetail = Loadable(lazy(() => import('pages/collaborations/campaign-detail')));
const SamplesManagement = Loadable(lazy(() => import('pages/collaborations/samples-management')));
const CampaignSetupTiktokTab = Loadable(lazy(() => import('features/collaboration-setup/components/main_tts')));
const CampaignSetupYoutubeTab = Loadable(lazy(() => import('features/collaboration-setup/components/main_yts')));
const TTSSamplesManagement = Loadable(lazy(() => import('features/samples-management/components/main_tts')));
const YTSSamplesManagement = Loadable(lazy(() => import('features/samples-management/components/main_yts')));
const MyLists = Loadable(lazy(() => import('pages/affiliate-community/my-lists')));
const MyListDetail = Loadable(lazy(() => import('pages/affiliate-community/my-list-detail')));

const CreateDynamicSequence = Loadable(lazy(() => import('pages/crm/create-sequence-dynamic')));
const CreateOneTimeSequence = Loadable(lazy(() => import('pages/crm/create-sequence-one-time')));
const CreateSequence = Loadable(lazy(() => import('pages/crm/create-sequence-picker')));
const CreateTemplate = Loadable(lazy(() => import('pages/crm/create-template')));
const MySequences = Loadable(lazy(() => import('pages/crm/my-sequences')));
const MyTemplates = Loadable(lazy(() => import('pages/crm/my-templates')));
const SequenceDetail = Loadable(lazy(() => import('pages/crm/sequence-detail')));
const TemplateDetail = Loadable(lazy(() => import('pages/crm/template-detail')));
const EditSequence = Loadable(lazy(() => import('pages/crm/edit-sequence')));
const DuplicateSequence = Loadable(lazy(() => import('pages/crm/duplicate-sequence')));

const AccountTabAccount = Loadable(lazy(() => import('sections/profiles/account/TabAccount')));
const AccountTabPassword = Loadable(lazy(() => import('sections/profiles/account/TabPassword')));

const Billing = Loadable(lazy(() => import('pages/profiles/billing')));
const BillingTabPaymentMethods = Loadable(lazy(() => import('features/pricing/components/TabPaymentMethods')));
const BillingTabPayments = Loadable(lazy(() => import('features/pricing/components/TabPayments')));

const MessagesCenter = Loadable(lazy(() => import('pages/message-center')));

const SparkCodeRequests = Loadable(lazy(() => import('pages/crm/automations/spark-code-requests')));
const ContentReminders = Loadable(lazy(() => import('pages/crm/automations/content-reminders')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        {
          path: 'home',
          element: <Home />
        },
        {
          path: 'settings',
          element: <Settings />
        },
        {
          path: SHOP_HEALTH_PATH,
          element: <ShopHealth />
        },
        {
          path: COMMISSION_OPTIMIZER_PATH,
          element: <CommissionOptimizer />
        },
        {
          path: 'affiliates',
          children: [
            { path: 'affiliates-finder', element: <AffiliatesFinder /> },
            { path: 'my-community', element: <AffiliateCommunity /> },
            { path: 'my-lists', element: <MyLists /> },
            { path: 'my-lists/editing/:id', element: <AffiliateCommunity /> },
            { path: 'my-lists/:id', element: <MyListDetail /> }
          ]
        },
        {
          path: 'collaborations',
          children: [
            {
              path: 'campaign-setup',
              element: <CampaignSetup />,
              children: [
                {
                  path: 'tiktok-shop',
                  element: <CampaignSetupTiktokTab />
                },
                {
                  path: 'youtube-shopping',
                  element: <CampaignSetupYoutubeTab />
                }
              ]
            },
            {
              path: 'collaboration-review',
              element: <CollaborationReview />
            },
            {
              path: 'id/:campaignId',
              element: <CampaignDetail />
            },
            {
              path: 'samples-management',
              element: <SamplesManagement />,
              children: [
                {
                  path: 'tiktok-shop',
                  element: <TTSSamplesManagement />
                },
                {
                  path: 'youtube-shopping',
                  element: <YTSSamplesManagement />
                }
              ]
            }
          ]
        },
        {
          path: 'crm',
          children: [
            {
              path: 'create-sequence/dynamic',
              element: <CreateDynamicSequence />
            },
            {
              path: 'create-sequence/dynamic/:id',
              element: <CreateDynamicSequence />
            },
            {
              path: 'create-sequence/one-time',
              element: <CreateOneTimeSequence />
            },
            {
              path: 'create-sequence/one-time/:id',
              element: <CreateOneTimeSequence />
            },
            {
              path: 'create-sequence',
              element: <CreateSequence />
            },
            { path: 'duplicate-sequence/:id', element: <DuplicateSequence /> },
            {
              path: 'edit-sequence/:id',
              element: <EditSequence />
            },
            {
              path: 'my-sequences',
              element: <MySequences />
            },
            {
              path: 'my-sequences/:id',
              element: <SequenceDetail />
            },
            {
              path: 'create-template',
              element: <CreateTemplate />
            },
            {
              path: 'my-templates',
              element: <MyTemplates />
            },
            {
              path: 'my-templates/:id',
              element: <TemplateDetail />
            },
            {
              path: 'automations',
              children: [
                { path: 'spark-code-requests', element: <SparkCodeRequests /> },
                { path: 'content-reminders', element: <ContentReminders /> }
              ]
            }
          ]
        },
        {
          path: 'video-ai',
          element: <VideoAi />
        },
        {
          path: 'video-archetypes',
          element: <VideoArchetypes />
        },
        {
          path: 'data-analytics',
          element: <DataAnalytics />,
          children: [
            {
              path: 'tiktok-shop',
              element: <DataAnalyticsTiktokTab />,
              children: [
                { index: true },
                { path: 'performance-overview', element: <PerformanceOverviewTab /> },
                { path: 'performance-dashboard', element: <PerformanceDashboardTab /> },
                { path: 'outreach-performance', element: <OutreachPerformanceTab /> },
                { path: 'ad-performance', element: <AdPerformanceTab /> },
                { path: 'product-performance', element: <ProductPerformanceTab /> },
                { path: 'samples-by-tier-dashboard', element: <SamplesByTierDashboardTab /> },
                { path: 'collaboration-performance', element: <CollaborationPerformanceTab /> },
                { path: 'video-performance', element: <VideoPerformanceTab /> },
                { path: 'top-creators', element: <TopCreatorsTab /> }
              ]
            },
            {
              path: 'youtube-shopping',
              element: <DataAnalyticsYoutubeTab />
            }
          ]
        },
        {
          path: 'market-intelligence',
          children: [
            { path: 'best-selling-products', element: <BestSellingProducts /> },
            { path: 'best-selling-creators', element: <BestSellingCreators /> },
            { path: 'best-selling-videos', element: <BestSellingVideos /> },
            { path: 'best-selling-lives', element: <BestSellingLives /> }
          ]
        },
        {
          path: 'profiles',
          children: [
            {
              path: 'account',
              children: [
                {
                  path: 'my-account',
                  element: <AccountTabAccount />
                },
                {
                  path: 'pricing',
                  element: <Pricing />
                },
                {
                  path: 'password',
                  element: <AccountTabPassword />
                }
              ]
            },
            {
              path: 'billing',
              element: <Billing />,
              children: [
                { path: 'payment-methods', element: <BillingTabPaymentMethods /> },
                { path: 'payments', element: <BillingTabPayments /> }
              ]
            }
          ]
        },
        {
          path: 'add-payment',
          element: <AddPayment />
        },
        {
          path: 'message-center',
          element: <MessagesCenter />
        },
        {
          path: 'support',
          children: [
            {
              path: 'help',
              element: <Help />
            },

            {
              path: 'faqs',
              element: <Faqs />
            }
          ]
        }
      ]
    },
    {
      path: '/maintenance',
      element: <PagesLayout />,
      children: [
        {
          path: '404',
          element: <Error404 />
        }
      ]
    },
    // Catch-all route for 404
    {
      path: '*',
      element: <Error404 />
    }
  ]
};

export default MainRoutes;
